import React, { FC, useEffect, useState } from 'react';
import { Divider, Text, TextButton } from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import css from '../TemplatesMainPage.scss';
import classNames from 'classnames';
import { useTabs } from '../../../hooks/useTabs';
import {
  SideBarItemType,
  SIDEBAR_ITEMS,
} from '../../../constants/sideBarItems';
import { Animator } from 'wix-animations';
import { DataHooks } from '../../../constants/dataHooks';
import { getDivRefPosition } from '../../../utils/getDivRefPosition';
import { TemplateTab } from '../../../types/TemplateTabs';

type Props = {
  mainGridRef: React.RefObject<HTMLDivElement>;
};

export const MainPageSideBar: FC<Props> = ({ mainGridRef }) => {
  const { activeTabId, onTabClick } = useTabs();
  const { t } = useTranslation();
  const [sideBarTimer, setSideBarTimer] = useState(false);
  const [shouldScrollIntoView, setShouldScrollIntoView] =
    useState<boolean>(false);
  const shouldScrollRef = React.useRef(false); // to memoize and spare setStateActions

  useEffect(() => {
    const handleScroll = () => {
      const topBoundingMainGrid = getDivRefPosition(mainGridRef, 'top');
      const shouldScroll = topBoundingMainGrid < 0;
      if (shouldScroll !== shouldScrollRef.current) {
        setShouldScrollIntoView(shouldScroll);
        shouldScrollRef.current = shouldScroll;
      }
    };
    document.addEventListener('scroll', handleScroll);
    setTimeout(() => setSideBarTimer(true), 100);
    return () => document.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div data-hook={DataHooks.MainPageSideBar} className={css.stickyBar}>
      <div className={css.animatorWrapper}>
        <Animator
          height
          opacity
          translate="top"
          show={sideBarTimer}
          timing="slow02"
        >
          <div className={css.sideBarItemsWrapper}>
            {SIDEBAR_ITEMS.map((tab) => {
              const isSelectedTab = activeTabId === tab.id;
              const type = tab.type;
              if (type === SideBarItemType.HEADING) {
                return (
                  <>
                    <div
                      key={tab.id.concat('-divider')}
                      className={css.sideBarItemDivider}
                    />
                    <Text
                      size="small"
                      key={tab.id}
                      className={css.sideBarHeadingItem}
                    >
                      {t(tab.sideBarItemKey)}
                    </Text>
                  </>
                );
              } else {
                return (
                  <TextButton
                    key={tab.id}
                    skin="dark"
                    size="medium"
                    weight={isSelectedTab ? 'normal' : 'thin'}
                    dataHook={DataHooks.TabItem(tab.id)}
                    className={classNames(css.tabTextButton, {
                      [css.selectedTabTextButton]: isSelectedTab,
                    })}
                    onClick={() => {
                      onTabClick(
                        tab.id as TemplateTab,
                        mainGridRef,
                        shouldScrollIntoView,
                      );
                    }}
                  >
                    {t(tab.sideBarItemKey)}
                  </TextButton>
                );
              }
            })}
          </div>
        </Animator>
      </div>
    </div>
  );
};
